import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Eye, EyeSlash, ArrowRight } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) {
      toast.success("Welcome back");
      navigate("/");
    } else {
      setError(res.error);
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-zinc-100" data-testid="login-page">
      {/* Left: brand panel */}
      <div className="lg:col-span-3 relative hidden lg:block overflow-hidden border-r border-zinc-200">
        <img
          src="https://images.unsplash.com/photo-1769144256227-5185141c3aca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwyfHxzaGlwcGluZyUyMGNvbnRhaW5lcnMlMjBwb3J0JTIwYWVyaWFsfGVufDB8fHx8MTc3OTM3MjI1OXww&ixlib=rb-4.1.0&q=85"
          alt="containers"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="font-black tracking-tighter text-3xl" style={{ fontFamily: "Chivo" }}>
            MULTI<span style={{ color: "#7DA3FF" }}> SOLUTIONS</span>
          </div>
          <div>
            <div className="label-mini text-white/70">Operations Console</div>
            <h1 className="text-5xl xl:text-6xl font-bold tracking-tight mt-2 max-w-xl leading-[0.95]">
              The back-office for sourcing,
              <br />shipping <span className="text-[#7DA3FF]">&amp; settling.</span>
            </h1>
            <p className="text-sm text-white/70 mt-6 max-w-md leading-relaxed">
              Track quotations, multi-currency ledgers, supplier payments and door-to-door delivery
              status — all from a single console built for the family-run import business.
            </p>
            <div className="grid grid-cols-3 gap-px bg-white/10 mt-10 max-w-md">
              {[
                ["PKR · USD · CNY", "Currencies"],
                ["5 Roles", "Access Control"],
                ["Audit Trail", "Append-Only"],
              ].map(([n, l]) => (
                <div key={l} className="bg-black/40 p-4">
                  <div className="text-base font-bold" style={{ fontFamily: "Chivo" }}>{n}</div>
                  <div className="label-mini text-white/70 mt-1">{l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="text-[11px] text-white/40 tracking-wide">
            © {new Date().getFullYear()} Multi Solutions — internal tool
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="lg:col-span-2 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="lg:hidden font-black tracking-tighter text-2xl mb-8" style={{ fontFamily: "Chivo" }}>
            MULTI<span style={{ color: "#0020C2" }}> SOLUTIONS</span>
          </div>
          <div className="label-mini">Sign in</div>
          <h2 className="text-3xl font-bold tracking-tight mt-1">Welcome back.</h2>
          <p className="text-sm text-zinc-500 mt-2">
            Enter your credentials to access the console.
          </p>

          <form className="mt-8 space-y-4" onSubmit={onSubmit}>
            <div>
              <label className="label-mini">Email</label>
              <Input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@multisols.xyz"
                className="mt-1 rounded-sm h-10"
                required
              />
            </div>
            <div>
              <label className="label-mini">Password</label>
              <div className="relative mt-1">
                <Input
                  data-testid="login-password"
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="rounded-sm h-10 pr-10"
                  required
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 p-1"
                  onClick={() => setShow((s) => !s)}
                  data-testid="toggle-password"
                >
                  {show ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="text-xs text-red-700 border border-red-200 bg-red-50 px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit"
              disabled={busy}
              className="w-full h-11 rounded-sm bg-black text-white hover:bg-zinc-800 font-semibold tracking-tight"
            >
              {busy ? "Signing in…" : (<>Sign in <ArrowRight size={14} className="ml-2" /></>)}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
